export {
  getContentUrl,
  getCssUrlPrefix,
  getPreBootstrapScriptTag,
  isXDomain,
  loadLocalizedStrings,
  loadPageNotFoundScript,
  loadPublicErrorCss,
  loadStaticAssets,
  onViewerBootstrapLoaded,
  onVqlScriptReady,
  preloadStaticAssets,
};

import { TsConfig } from "./API/TsConfig";
import {
  DefaultFallbackLocale,
  findFirstServerSupportedLanguageOrDefault,
  getUserPreferredBrowserLanguages,
  localeToLanguageRegionString,
  tryGetServerSupportedLanguage,
} from "./Localization";
import { isAuthoring, isMobile } from "./StaticConfig";
import { getQueryFlag } from "./UserShowOptions";
import {
  EptBreakdownMetricNames,
  closeEptMetricsContext,
  openEptMetricsContext,
} from "./telemetry/TelemetryBreakdown";

let readyToBootstrap = false;
let scriptReady = false;

function onViewerBootstrapLoaded(callback: () => void) {
  if (readyToBootstrap) {
    callback();
  } else {
    document.addEventListener("viewerBootstrapLoaded", () => {
      callback();
    });
  }
}

export function fireViewerBootstrapLoaded() {
  readyToBootstrap = true;
  document.dispatchEvent(new CustomEvent("viewerBootstrapLoaded"));
  closeEptMetricsContext(EptBreakdownMetricNames.loadViewerBootstrapModule);
}

function onVqlScriptReady(callback: () => void) {
  if (scriptReady) {
    callback();
  } else {
    document.addEventListener("vqlScriptReady", () => {
      callback();
    });
  }
}

// Test only export
export function fireScriptReady() {
  scriptReady = true;
  document.dispatchEvent(new CustomEvent("vqlScriptReady"));
  closeEptMetricsContext(EptBreakdownMetricNames.loadVqlModules);
}

function loadLocalizedStrings(
  doc: Document,
  languageLocale: Intl.Locale
): Promise<void> {
  const assetPrefix = getJsUrlPrefix(doc);
  const languageSuffix = getJsStringsLanguage(languageLocale);
  return loadScript(
    `${assetPrefix}/jsstrings_${languageSuffix}.js`,
    isXDomain(),
    doc
  );
}

function getViewerBootstrapFileUrl(isJsDebug: boolean, doc: Document): string {
  const jsAssetLocation = getJsUrlPrefix(doc);
  const dotDebugSuffix = isJsDebug ? ".debug" : "";
  const viewerBootstrapFile = `${jsAssetLocation}/ViewerBootstrap${dotDebugSuffix}.js`;

  return viewerBootstrapFile;
}

export function getServerLanguageAndLocale(tsConfig: TsConfig): {
  language: Intl.Locale;
  locale: Intl.Locale;
} {
  const serverLanguage = new Intl.Locale(tsConfig.language.replace("_", "-"));
  const serverLocale = new Intl.Locale(tsConfig.locale.replace("_", "-"));

  // use the locale for the language if the language does not have a region and the language codes match
  // for example: serverLanguage="en" serverLocale="en-GB", want to use "en-GB" as the language
  // (this is assuming the locale is an actual supported language)
  // else find the supported language for the given serverLanguage
  let language: Intl.Locale;
  if (
    serverLanguage.region === undefined &&
    serverLanguage.language === serverLocale.language
  ) {
    // make sure serverLocale is actually a supported language
    const supportedLanguageForLocale =
      tryGetServerSupportedLanguage(serverLocale);
    language =
      supportedLanguageForLocale !== undefined
        ? supportedLanguageForLocale
        : tryGetServerSupportedLanguage(serverLanguage) ||
          DefaultFallbackLocale;
  } else {
    language =
      tryGetServerSupportedLanguage(serverLanguage) || DefaultFallbackLocale;
  }

  return { language, locale: serverLocale };
}

function loadStaticAssets(
  doc: Document,
  tsConfig: TsConfig,
  isJsDebug: boolean
) {
  const xdomainActive = isXDomain();

  const isAuthoring = tsConfig.is_authoring;
  const isMobile = tsConfig.is_mobile;
  const { language, locale } = getServerLanguageAndLocale(tsConfig);

  const jsStaticAssetFiles = getJsAssets(
    isJsDebug,
    isMobile,
    isAuthoring,
    language,
    locale
  );

  const viewerBootstrapFile = getViewerBootstrapFileUrl(isJsDebug, doc);

  openEptMetricsContext(EptBreakdownMetricNames.loadViewerBootstrapModule);
  loadScript(viewerBootstrapFile, xdomainActive, doc)
    .then(() => {
      fireViewerBootstrapLoaded();
    })
    .catch((reason: Error) =>
      console.error(
        `Bootstrap failed with unhandled error: ${reason.toString()}`
      )
    );

  openEptMetricsContext(EptBreakdownMetricNames.loadCss);
  const cssStaticAssetFiles = getCssAssets(isJsDebug, isMobile, isAuthoring);
  cssStaticAssetFiles.forEach((x: string) => {
    loadCss(x, doc).catch((reason: Error) =>
      console.error(`Error loading resource ${x}: ${reason.toString()}`)
    );
  });
  closeEptMetricsContext(EptBreakdownMetricNames.loadCss);

  openEptMetricsContext([EptBreakdownMetricNames.loadVqlModules]);
  onViewerBootstrapLoaded(() => {
    const modulesLoaded = jsStaticAssetFiles.map((x: string) => {
      return loadScript(x, xdomainActive, doc);
    });
    Promise.all(modulesLoaded)
      .then(() => {
        fireScriptReady();
      })
      .catch((reason: Error) =>
        console.error(`Vql script load error: ${reason.toString()}`)
      );
  });
}

function loadScript(
  scriptUrl: string,
  xdomain: boolean,
  doc: Document,
  loadAsynchronously = false
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const script = doc.createElement("script");
    script.type = "text/javascript";
    script.src = scriptUrl;
    script.async = loadAsynchronously;
    script.onload = () => {
      resolve();
    };
    script.onerror = () => {
      reject();
    };
    if (xdomain) {
      script.crossOrigin = "anonymous";
    }
    doc.head.appendChild(script);
  });
}

function loadPageNotFoundScript(doc: Document) {
  const jsAssetLocation = getJsUrlPrefix(document);
  const scriptUrl = `${jsAssetLocation}/vizqlserver/404.js`;
  return loadScript(scriptUrl, isXDomain(), doc);
}

function loadPublicErrorCss(doc: Document) {
  const stylesheets = getContentUrl("stylesheets");
  const publicErrorStylesheets = [
    `${stylesheets}/base_packaged.css`,
    `${stylesheets}/404.css`,
  ];
  return Promise.all(
    publicErrorStylesheets.map((cssUrl) => {
      return loadCss(cssUrl, doc);
    })
  );
}

function loadCss(cssUrl: string, doc: Document) {
  return new Promise<void>((resolve, reject) => {
    const link = document.createElement("link");
    link.type = "text/css";
    link.href = cssUrl;
    link.media = "all";
    link.rel = "Stylesheet";
    link.onload = () => {
      resolve();
    };
    link.onerror = () => {
      reject();
    };
    doc.head.appendChild(link);
  });
}

function getCssUrlPrefix(doc: Document): string {
  return getContentUrl("css", doc);
}

function getJsUrlPrefix(doc: Document): string {
  return getContentUrl("javascripts", doc);
}

function getPreBootstrapScriptTag(doc: Document): HTMLScriptElement | null {
  const scriptTags = Array.from(doc.scripts);

  if (scriptTags.length) {
    const preBootstrapScriptTag = scriptTags.find((script) =>
      script.src.includes("PreBootstrap")
    );

    return preBootstrapScriptTag || null;
  }

  return null;
}

// We need to find the PreBootstrap.js script tag on the page and get it's prefix
// Prepend that prefix to the url that is passed in
function getContentUrl(url: string, doc: Document | undefined = undefined) {
  const preBootstrapScriptTag = getPreBootstrapScriptTag(doc ?? document);

  if (preBootstrapScriptTag) {
    const urlPrefix = preBootstrapScriptTag.src.substring(
      0,
      preBootstrapScriptTag.src.indexOf("/javascripts")
    );

    return `${urlPrefix}/${url}`;
  }

  return url;
}

function isXDomain() {
  const scriptTag = getPreBootstrapScriptTag(document);
  return (scriptTag && scriptTag.crossOrigin === "anonymous") || false;
}

function getCssAssets(
  isDebug: boolean,
  isMobile: boolean,
  isAuthoring: boolean
): string[] {
  const cssAssets = [];
  const minifiedSuffix = isDebug ? "" : "-min";
  const clientSuffix = isMobile ? "mobile" : "web";
  const debugSuffix = isDebug ? ".debug" : "";

  const cssPrefix = getCssUrlPrefix(window.document);
  if (cssPrefix && isAuthoring) {
    cssAssets.push(`${cssPrefix}/codemirror${minifiedSuffix}.css`);
    cssAssets.push(`${cssPrefix}/vqldatatab${clientSuffix}${debugSuffix}.css`);
    cssAssets.push(
      `${cssPrefix}/vqlcategoricalbindialog${clientSuffix}${debugSuffix}.css`
    );
  }

  return cssAssets;
}

export function getJsStringsLanguage(localeOrLanguage: Intl.Locale): string {
  if (getQueryFlag(":pseudo")) {
    return "pseudo";
  }

  const matchingLocale = tryGetServerSupportedLanguage(localeOrLanguage);
  const supportedLanguage =
    matchingLocale === undefined ? DefaultFallbackLocale : matchingLocale;

  // Test to see if the supported locale is the default for the language
  // If it is then the suffix is just the language code (without the region)
  // Otherwise then the suffix is language_region
  const matchingLanguageOnlyLocale = tryGetServerSupportedLanguage(
    new Intl.Locale(supportedLanguage.language)
  );

  const supportedLanguageCode = localeToLanguageRegionString(supportedLanguage);

  const languageSuffix =
    matchingLanguageOnlyLocale !== undefined &&
    localeToLanguageRegionString(matchingLanguageOnlyLocale) ===
      supportedLanguageCode
      ? supportedLanguage.language
      : supportedLanguageCode;

  return languageSuffix;
}

function getJsAssets(
  isJsDebug: boolean,
  isMobile: boolean,
  isAuthoring: boolean,
  language: Intl.Locale,
  locale: Intl.Locale
): string[] {
  const minifiedDashSuffix = isJsDebug ? "" : "-min";
  const minifiedDotSuffix = isJsDebug ? "" : ".min";
  const clientSuffix = isMobile ? "mobile" : "web";
  // use language code for jsstrings if locale does not match language
  // (this can happen when region is not set by the user)

  const jsStringsLanguageSuffix = getJsStringsLanguage(language);
  const messagesLanguageSuffix = localeToLanguageRegionString(
    tryGetServerSupportedLanguage(language) || DefaultFallbackLocale
  );
  const formattersAndParsersLanguageSuffix =
    localeToLanguageRegionString(locale);

  const jsAssetLocation = getJsUrlPrefix(document);

  const jsAssets = [
    `${jsAssetLocation}/jquery${minifiedDotSuffix}.js`,
    `${jsAssetLocation}/mscorlib${minifiedDotSuffix}.js`,
    `${jsAssetLocation}/jsstrings_${jsStringsLanguageSuffix}.js`,
    `${jsAssetLocation}/messages.${messagesLanguageSuffix}.js`,
    `${jsAssetLocation}/formatters-and-parsers.${formattersAndParsersLanguageSuffix}.js`,
  ];

  if (isAuthoring) {
    jsAssets.push(`${jsAssetLocation}/codemirror${minifiedDashSuffix}.js`);
  }

  if (isJsDebug) {
    jsAssets.push(
      `${jsAssetLocation}/vqlbrowsersupport.debug.js`,
      `${jsAssetLocation}/vqlfiledropresponder.debug.js`,
      `${jsAssetLocation}/vqlcore.debug.js`,
      `${jsAssetLocation}/vqldomain.debug.js`,
      `${jsAssetLocation}/vqlauthoringdomain.debug.js`,
      `${jsAssetLocation}/iscroll.js`,
      `${jsAssetLocation}/shared-widgets.js`,
      `${jsAssetLocation}/eventutils.js`,
      `${jsAssetLocation}/spiffui.debug.js`,
      `${jsAssetLocation}/spiffui${clientSuffix}.debug.js`,
      `${jsAssetLocation}/tabruntime.debug.js`,
      `${jsAssetLocation}/vqlmoduleinterfaces.debug.js`,
      `${jsAssetLocation}/vqllegacydojomoduleinterfaces.debug.js`,
      `${jsAssetLocation}/vqlui.debug.js`,
      `${jsAssetLocation}/vqlruntimerendered.debug.js`,
      `${jsAssetLocation}/vqlvizzone.debug.js`,
      `${jsAssetLocation}/vqlvizzonedesktop.debug.js`,
      `${jsAssetLocation}/vqllegendzone.debug.js`,
      `${jsAssetLocation}/require.js`,
      `${jsAssetLocation}/vqlruntimeui.debug.js`,
      `${jsAssetLocation}/vqlui${clientSuffix}.debug.js`,
      `${jsAssetLocation}/vqlvizzone${clientSuffix}.debug.js`,
      `${jsAssetLocation}/vqllegendzone${clientSuffix}.debug.js`,
      `${jsAssetLocation}/vqlversion.js`
    );

    if (isAuthoring) {
      jsAssets.push(
        `${jsAssetLocation}/vqlauthoring.debug.js`,
        `${jsAssetLocation}/vqlresizeheadersmodule.debug.js`,
        `${jsAssetLocation}/vqlauthoring${clientSuffix}.debug.js`
      );
    }
  } else {
    jsAssets.push(`${jsAssetLocation}/vql${clientSuffix}.js`);
    if (isAuthoring) {
      jsAssets.push(`${jsAssetLocation}/vqlauthoring${clientSuffix}.js`);
    }
  }

  return jsAssets;
}

function preloadStaticAssets(doc: Document, isJsDebug: boolean) {
  const xdomainActive = isXDomain();

  const isAuthoringMode = isAuthoring(doc) || false;
  const isMobileClient = isMobile(doc) || false;
  const preferredLanguages = getUserPreferredBrowserLanguages();
  const locale = findFirstServerSupportedLanguageOrDefault(preferredLanguages);

  const jsStaticAssetFiles = getJsAssets(
    isJsDebug,
    isMobileClient,
    isAuthoringMode,
    locale,
    locale
  );

  jsStaticAssetFiles.unshift(getViewerBootstrapFileUrl(isJsDebug, doc));

  jsStaticAssetFiles.map((x: string) => {
    return preloadScript(x, xdomainActive, doc);
  });
}

function preloadScript(scriptUrl: string, xdomain: boolean, doc: Document) {
  const script = document.createElement("link");
  script.rel = "preload";
  script.href = scriptUrl;
  script.as = "script";

  if (xdomain) {
    script.crossOrigin = "anonymous";
  }
  doc.head.appendChild(script);
}
